import toNumber from 'lodash/toNumber';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { OrderAppGwpProduct } from '@/graphql/orderApp/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useFetchGwpName } from '@/hooks/useFetchGwpName';
import type { WithChildren } from '@/lib/types';
import { checkoutMessages } from '@/messages/checkout';
import { useRegionProvider } from '@/providers/RegionProvider';
import { GWP } from '@/states/checkout';

import GwpImage from '@/assets/gwp.png';
import GwpEmpty from '@/assets/gwp-empty.png';

import * as S from './GiftWithPurcahse.styles';

export const checkGwpProductAvailable = (
  product: OrderAppGwpProduct,
  totalAmount: number,
  registerMyWatch: boolean,
) => {
  const available = toNumber(product.threshhold) <= totalAmount;

  return available && registerMyWatch;
};

type GiftDescriptionProps = OrderAppGwpProduct & { channel: string };
export const GiftDescription = (props: GiftDescriptionProps) => {
  const {
    query: { languageCode },
  } = useRegionProvider();
  const id = props.id;
  const result = useFetchGwpName(
    languageCode,
    id,
    'gift',
    props.channel,
    props.name,
  );

  return <S.GiftDesc data-test-id='GwpGiftDescription'>{result}</S.GiftDesc>;
};

export const GiftWithPurchase = ({
  activeGwp,
  gifts: data,
  loading,
  onGwpChange,
}: {
  activeGwp?: string;
  gifts?: OrderAppGwpProduct[];
  loading: boolean;
  onGwpChange: (value: string) => void;
}) => {
  const {
    snapshot: { checkout },
  } = useCheckout<true>();

  const { formatMessage } = useIntl();
  const amount = toNumber(checkout.totalPrice.gross.amount);
  const { watch } = useFormContext();
  const registerMyWatch = watch('registerMyWatch');

  const onSelectedGift = async (giftId: string) => {
    if (registerMyWatch && giftId != activeGwp) {
      onGwpChange(giftId);
    }
  };

  if (loading || !data?.length) return null;

  return (
    <Section>
      <Header>{formatMessage(checkoutMessages.gwpCheckoutTitle)}</Header>
      <Description>
        {formatMessage(checkoutMessages.gwpCheckoutPrompt)}
      </Description>
      <GiftsWrapper>
        <Gift disabled={!registerMyWatch}>
          <S.GiftImageWrapper
            $active={registerMyWatch && activeGwp === GWP.doNotNeedGift}
            disabled={!registerMyWatch}
            onClick={() => onSelectedGift(GWP.doNotNeedGift)}
          >
            <S.GiftImage
              alt='gwp image'
              data-test-id='EmptyGwpImage'
              height='128'
              priority
              src={GwpEmpty}
              width='128'
            />
          </S.GiftImageWrapper>
          <S.GiftDesc>{formatMessage(checkoutMessages.noNeedGift)}</S.GiftDesc>
        </Gift>
        {data.map((product) => (
          <Gift
            key={product.id}
            disabled={
              !checkGwpProductAvailable(product, amount, registerMyWatch)
            }
            onClick={() =>
              checkGwpProductAvailable(product, amount, registerMyWatch) &&
              onSelectedGift(product.id)
            }
          >
            <S.GiftImageWrapper
              $active={registerMyWatch && activeGwp === product.id}
              disabled={
                !checkGwpProductAvailable(product, amount, registerMyWatch)
              }
            >
              <S.GiftImage
                key={'image' + product.id}
                alt={product.thumbnail_alt ?? ''}
                data-test-id='GwpImage'
                height={128}
                loading='eager'
                priority
                src={product.thumbnail_url ?? GwpImage}
                width={128}
              />
            </S.GiftImageWrapper>
            <GiftDescription {...product} channel={checkout.channel.slug} />
          </Gift>
        ))}
      </GiftsWrapper>
    </Section>
  );
};

export const Section = ({ children }: WithChildren) => (
  <S.Section data-test-id='GWPSection'>{children}</S.Section>
);

export const Header = ({ children }: WithChildren) => (
  <S.Header data-test-id='GWPHeader'>{children}</S.Header>
);

export const Description = ({ children }: WithChildren) => (
  <S.Description data-test-id='GWPDescription'>{children}</S.Description>
);

export const GiftsWrapper = ({ children }: WithChildren) => (
  <S.Gifts data-test-id='GWPGiftsWrapper'>{children}</S.Gifts>
);

interface GiftImageWrapperProps extends WithChildren {
  $active: boolean;
  disabled?: boolean;
}

export const GiftImageWrapper = ({
  $active,
  children,
  disabled,
}: GiftImageWrapperProps) => (
  <S.GiftImageWrapper
    $active={$active}
    data-test-id='GWPGiftsWrapper'
    disabled={!!disabled}
  >
    {children}
  </S.GiftImageWrapper>
);

interface GiftProps extends WithChildren {
  disabled?: boolean;
  onClick?: () => void;
}

export const Gift = ({ children, disabled, onClick }: GiftProps) => (
  <S.Gift data-test-id='GWPGift' disabled={!!disabled} onClick={onClick}>
    {children}
  </S.Gift>
);
